import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  exportToExcel,
  getErrorResponse,
  getHeader,
  roundToMaxDecimals,
  toastError,
} from "../../utils/util";
import { Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import CustomCircularP from "../../widgets/CustomCircularP";
import { toast } from "react-toastify";
import axios from "axios";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import PageTitle from "../../widgets/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { debounce } from "lodash";
import DataTable from "../../widgets/DataTable";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import { LoadingButton } from "@mui/lab";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import { UserContext } from "../../context/UserProvider";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
const AccountsList = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = user?.userType === "SuperAdmin";
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [showDelete, setShowDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toDelete, setToDelete] = useState("");
  const [partner, setPartner] = useState("");
  const [assignedEmployee, setAssignedEmployee] = useState("");
  const [name, setName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [sq, setSq] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSq(value);
        setPage(1);
      }, 500),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (value) => {
    setName(value);
    debouncedSearch(value);
  };

  const clearFilters = () => {
    debouncedSearch.cancel();
    setName("");
    setSq("");
    setPartner("");
    setAssignedEmployee("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(name || partner || assignedEmployee);
  const { data: employeeData, isLoading: loadingEmployees } = useApiEp(
    "/root/employees",
    isSuperAdmin
  );
  const employees = employeeData?.employees ?? [];
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-accounts?page=${page}&limit=${limit}&name=${encodeURIComponent(
      sq
    )}&partner=${encodeURIComponent(
      partner
    )}&assignedToEmployee=${encodeURIComponent(assignedEmployee)}`
  );
  const handleAction = useCallback((action, id) => {
    switch (action) {
      case "view":
        window.open(`/home/${id}/account-details`, "_blank"); // Open in new tab
        break;
      case "edit":
        window.open(`/home/accounts/${id}/edit-account`, "_blank"); // Open in new tab
        break;
      case "delete":
        setToDelete(id);
        setShowDelete(true);
        break;
      default:
        console.warn("unknown action", id);
    }
  }, []);

  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 55%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <PageTitle>Accounts</PageTitle>
              <Chip
                label={`${data?.totalItems || 0} total`}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Find, review, and manage customer accounts.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            <Button
              onClick={() => navigate(`/home/add-user-manually`)}
              variant="outlined"
              startIcon={<AddOutlinedIcon />}
            >
              Add User
            </Button>
            <Button
              onClick={() => navigate(`/home/add-account`)}
              variant="contained"
              startIcon={<AddOutlinedIcon />}
            >
              Add Account
            </Button>
            <LoadingButton
              loading={exporting}
              onClick={async () => {
                try {
                  setExporting(true);
                  const response = await axios.get(
                    `${API_ENDPOINT}/root/get-all-accounts`,
                    getHeader()
                  );
                  exportToExcel(response.data, "accounts_list.xlsx");
                } catch (error) {
                  toastError(error);
                } finally {
                  setExporting(false);
                }
              }}
              variant="outlined"
              startIcon={<ImportExportIcon />}
            >
              Export
            </LoadingButton>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 3 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterAltOutlinedIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>
              Filters
            </Typography>
            {hasActiveFilters && (
              <Chip label="Active" size="small" color="primary" />
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<RestartAltOutlinedIcon />}
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Reset
          </Button>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: isSuperAdmin
                ? "minmax(220px, 1fr) 180px minmax(220px, 1fr)"
                : "minmax(240px, 1fr) 200px",
            },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            value={name}
            onChange={(e) => handleSearchChange(e.target.value)}
            label="Search accounts"
            placeholder="Customer ID, WABA ID, or phone ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={partner}
              label="Account Type"
              onChange={(event) => {
                setPartner(event.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All account types</MenuItem>
              <MenuItem value="haptik">Haptik</MenuItem>
              <MenuItem value="aisensy">Aisensy</MenuItem>
              <MenuItem value="rcs">RCS Clients</MenuItem>
              <MenuItem value="rcs2">RCS Clients 2</MenuItem>
              <MenuItem value="primary">Primary Accounts</MenuItem>
            </Select>
          </FormControl>
          {isSuperAdmin && (
            <FormControl size="small" fullWidth>
              <InputLabel>Assigned Employee</InputLabel>
              <Select
                value={assignedEmployee}
                label="Assigned Employee"
                disabled={loadingEmployees}
                onChange={(event) => {
                  setAssignedEmployee(event.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All employees</MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee._id} value={employee.employeeId}>
                    {employee.name} — {employee.employeeId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      <Stack
        direction={isSmallScreen ? "column" : "row"}
        spacing={1}
        mb={2}
        sx={{ flexWrap: "wrap" }}
      >
        <Button
          variant="text"
          fullWidth={isSmallScreen}
          onClick={() => navigate("/home/subscription-plan-stats")}
          startIcon={<MilitaryTechIcon />}
        >
          Plan Stats
        </Button>

        <Button
          variant="text"
          fullWidth={isSmallScreen}
          onClick={() => navigate("/home/manage-account-groups")}
          startIcon={<GroupWorkIcon />}
        >
          Account Groups
        </Button>
        <Button
          variant="text"
          fullWidth={isSmallScreen}
          onClick={() => navigate("/home/manage-reseller")}
          startIcon={<Users size={18} />}
        >
          Resellers
        </Button>
      </Stack>
      <CustomCircularP show={deleting || isLoading} />
      <SimpleYesNoDialog
        show={showDelete}
        setShow={setShowDelete}
        title={"Please confirm!"}
        message={"Are you sure you want to delete this item?"}
        onYes={async () => {
          try {
            setDeleting(true);
            setShowDelete(false);
            const response = await axios.delete(
              `${API_ENDPOINT}/root/delete-account/${toDelete}`,
              getHeader()
            );
            refetch();
            toast.success(response.data.message);
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
      />

      <DataTable
        sx={{ mb: 1 }}
        columns={[
          {
            id: "serial",
            label: "S.No.",
            render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
          },
          {
            id: "customerId",
            label: "CustomerID",
            render: (v, row) => {
              const subCount = Array.isArray(row.accounts)
                ? row.accounts.length
                : 0;

              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{v}</Typography>

                  <Chip
                    label={`${subCount} sub`}
                    size="small"
                    variant="outlined"
                    color={subCount > 0 ? "primary" : "default"}
                  />
                </Stack>
              );
            },
          },
          {
            id: "subscriptionPlan",
            label: "Waba Plan",
            render: (v) =>
              v ? ( // Check for explicit 0 or truthy value
                <Chip label={v} variant="outlined" size="small" color="info" />
              ) : (
                "NA"
              ),
          },
          {
            id: "assignedToEmployee",
            label: "Assigned Employee",
            render: (employeeId) => {
              if (!employeeId) {
                return (
                  <Chip label="Unassigned" size="small" variant="outlined" />
                );
              }

              const employee = employees.find(
                (item) => item.employeeId === employeeId
              );

              return (
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {employee?.name || "Employee"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {employeeId}
                  </Typography>
                </Box>
              );
            },
          },
          {
            id: "expiryDate",
            label: "Expiry Date",
            render: (v) =>
              v
                ? new Date(v).toLocaleString("en-IN", {
                    dateStyle: "medium",
                  })
                : "NA",
          },
          {
            id: "wallet",
            label: "WABA Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "voiceCallWallet",
            label: "Voice Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "rcsWalletRichMedia",
            label: "RCS Blnc",
            render: (v) =>
              v === 0 || v ? ( // Check for explicit 0 or truthy value
                <Chip
                  label={`${roundToMaxDecimals(v, 2)}`}
                  variant="outlined"
                  size="small"
                  color="info"
                />
              ) : (
                "NA"
              ),
          },
          {
            id: "createdOn",
            label: "Created On",
            render: (v) =>
              new Date(v).toLocaleString("en-IN", {
                dateStyle: "medium",
              }),
          },
        ]}
        actions={["view", "edit"]}
        rows={data?.data ?? []}
        page={page}
        itemsPerPage={limit}
        totalPages={data?.totalPages ?? 1}
        onPageChange={setPage}
        onAction={handleAction}
        itemsPerPageOptions={[20, 30, 50, 100]}
        onItemsPerPageChange={(lm) => {
          setLimit(lm);
        }}
        paginationAlign="end"
      />
    </Container>
  );
};

export default AccountsList;
